import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageHandlersRef = useRef([]);
  const notificationHandlersRef = useRef([]);
  const presenceHandlersRef = useRef([]);
  const readReceiptHandlersRef = useRef([]);

  // Fetch initial unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/unread/count`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
    } catch (e) { console.error('Error fetching unread count:', e); }
  }, [user?.id]);

  const connect = useCallback(() => {
    if (!user?.id) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${user.id}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        fetchUnreadCount();
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (user?.id) connect();
        }, 3000);
      };

      wsRef.current.onerror = () => {};

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'typing') {
            setTypingUsers(prev => ({ ...prev, [data.sender_id]: data.is_typing }));
            if (data.is_typing) {
              setTimeout(() => {
                setTypingUsers(prev => ({ ...prev, [data.sender_id]: false }));
              }, 3000);
            }
          } else if (data.type === 'message') {
            messageHandlersRef.current.forEach(handler => handler(data));
            setUnreadCount(prev => prev + 1);
          } else if (data.type === 'notification') {
            showNotification(data);
            notificationHandlersRef.current.forEach(handler => handler(data));
          } else if (data.type === 'subscription_updated') {
            window.dispatchEvent(new CustomEvent('bc-subscription-updated', { detail: data }));
          } else if (data.type === 'presence') {
            setOnlineUsers(prev => ({ ...prev, [data.user_id]: data.is_online }));
            presenceHandlersRef.current.forEach(handler => handler(data));
          } else if (data.type === 'read_receipt') {
            readReceiptHandlersRef.current.forEach(handler => handler(data));
          } else if (data.type === 'message_deleted') {
            messageHandlersRef.current.forEach(handler => handler(data));
          } else if (data.type === 'pong') {
            // keep-alive
          }
        } catch (error) {
          console.error('WS parse error:', error);
        }
      };
    } catch (error) {
      console.error('WS connect error:', error);
    }
  }, [user?.id, fetchUnreadCount]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (user?.id) {
      connect();
    } else {
      disconnect();
      setUnreadCount(0);
    }
    return () => disconnect();
  }, [user?.id, connect, disconnect]);

  // Keep-alive ping every 30 seconds
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    return () => clearInterval(pingInterval);
  }, []);

  const sendTyping = useCallback((recipientId, isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        recipient_id: recipientId,
        is_typing: isTyping
      }));
    }
  }, []);

  const showNotification = (data) => {
    const { notification_type, title, body } = data;
    if (notification_type === 'new_message') {
      toast.info(title, { description: body });
    } else if (notification_type === 'new_job') {
      toast.success(title, { description: body });
    } else if (notification_type === 'application_status') {
      if (data.status === 'accepted') toast.success(title, { description: body });
      else if (data.status === 'rejected') toast.error(title, { description: body });
      else toast.info(title, { description: body });
    } else {
      toast(title, { description: body });
    }
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    }
  };

  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current.push(handler);
    return () => { messageHandlersRef.current = messageHandlersRef.current.filter(h => h !== handler); };
  }, []);

  const addNotificationHandler = useCallback((handler) => {
    notificationHandlersRef.current.push(handler);
    return () => { notificationHandlersRef.current = notificationHandlersRef.current.filter(h => h !== handler); };
  }, []);

  const addPresenceHandler = useCallback((handler) => {
    presenceHandlersRef.current.push(handler);
    return () => { presenceHandlersRef.current = presenceHandlersRef.current.filter(h => h !== handler); };
  }, []);

  const addReadReceiptHandler = useCallback((handler) => {
    readReceiptHandlersRef.current.push(handler);
    return () => { readReceiptHandlersRef.current = readReceiptHandlersRef.current.filter(h => h !== handler); };
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  const decrementUnread = useCallback((count = 1) => {
    setUnreadCount(prev => Math.max(0, prev - count));
  }, []);

  const refreshUnread = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const value = {
    isConnected,
    typingUsers,
    onlineUsers,
    unreadCount,
    sendTyping,
    addMessageHandler,
    addNotificationHandler,
    addPresenceHandler,
    addReadReceiptHandler,
    requestNotificationPermission,
    decrementUnread,
    refreshUnread
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
